import { Command } from 'cmdk';
import { Check, ChevronDown, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Badge, Popover, PopoverContent, PopoverTrigger } from '@/components/ui';
import { cn } from '@/lib/cn';

export interface MultiOption {
  readonly value: string;
  readonly label: string;
  readonly hint?: string;
  /** Indent level for trees (categories). */
  readonly depth?: number;
  readonly keywords?: readonly string[];
}

export interface MultiSelectProps {
  /** Button text when nothing is selected, e.g. "Categories". */
  readonly label: string;
  readonly options: readonly MultiOption[];
  readonly value: readonly string[];
  readonly onChange: (value: string[]) => void;
  readonly icon?: ReactNode;
  readonly searchPlaceholder?: string;
  /** Expand a picked value (e.g. a parent category to include its children). */
  readonly expand?: (value: string) => readonly string[];
  readonly className?: string;
}

/** Filter-style multi-select: a compact trigger showing the count, a searchable checklist, and Clear. */
export const MultiSelect = ({ label, options, value, onChange, icon, searchPlaceholder = 'Search…', expand, className }: MultiSelectProps) => {
  const [open, setOpen] = useState(false);
  const selected = new Set(value);
  const toggle = (v: string) => {
    const group = expand ? expand(v) : [v];
    const next = new Set(selected);
    if (selected.has(v)) group.forEach((g) => next.delete(g));
    else group.forEach((g) => next.add(g));
    onChange([...next]);
  };
  const first = options.find((o) => selected.has(o.value));
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={value.length ? `${label}: ${value.length} selected` : label}
          className={cn(
            'inline-flex h-9 items-center gap-1.5 rounded-md border px-3 text-sm coarse:h-11 [&_svg]:size-4',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            value.length ? 'border-primary/50 bg-primary-soft text-text' : 'border-border-strong/60 bg-surface text-text-muted hover:text-text',
            className,
          )}
        >
          {icon}
          <span className="max-w-[10rem] truncate">{value.length === 1 && first ? first.label : label}</span>
          {value.length > 1 ? <Badge tone="primary">{value.length}</Badge> : null}
          <ChevronDown className="text-text-muted" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0">
        <Command loop>
          <div className="border-b border-border px-3">
            <Command.Input placeholder={searchPlaceholder} className="h-11 w-full bg-transparent text-sm text-text outline-none placeholder:text-text-muted" />
          </div>
          <Command.List className="max-h-72 overflow-y-auto p-1" aria-label={label}>
            <Command.Empty className="px-3 py-6 text-center text-sm text-text-muted">No matches.</Command.Empty>
            {options.map((o) => (
              <Command.Item
                key={o.value}
                value={`${o.label} ${o.value}`}
                keywords={o.keywords ? [...o.keywords] : undefined}
                onSelect={() => toggle(o.value)}
                aria-checked={selected.has(o.value)}
                role="option"
                className="flex min-h-10 cursor-pointer items-center gap-2 rounded-md px-2.5 text-sm text-text data-[selected=true]:bg-surface-sunken"
                style={o.depth ? { paddingLeft: `${0.625 + o.depth}rem` } : undefined}
              >
                <span
                  className={cn(
                    'flex size-4 shrink-0 items-center justify-center rounded border',
                    selected.has(o.value) ? 'border-primary bg-primary text-primary-foreground' : 'border-border-strong',
                  )}
                  aria-hidden
                >
                  {selected.has(o.value) ? <Check className="size-3" /> : null}
                </span>
                <span className="min-w-0 flex-1 truncate">{o.label}</span>
                {o.hint ? <span className="truncate text-xs text-text-muted">{o.hint}</span> : null}
              </Command.Item>
            ))}
          </Command.List>
        </Command>
        {value.length ? (
          <div className="border-t border-border p-1">
            <button
              type="button"
              onClick={() => onChange([])}
              className="flex min-h-10 w-full items-center gap-2 rounded-md px-2.5 text-sm text-text-muted hover:bg-surface-sunken hover:text-text"
            >
              <X className="size-4" aria-hidden /> Clear {label.toLowerCase()}
            </button>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
};
